import { createClient } from '@supabase/supabase-js';
import type { User, PickList, PickItem, PerformanceMetrics } from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseKey);

// Authentication Services
export const authService = {
  signInWithEmail: async (email: string, password: string) => {
    console.log('🔐 Attempting email sign in:', email);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    console.log('🔐 Email sign in result:', { data: data?.user?.id, error });
    return { data, error };
  },

  signInWithPin: async (pin: string) => {
    console.log('🔐 Attempting PIN sign in:', pin);
    // Custom PIN authentication logic
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('pin', pin)
      .single();
    
    console.log('🔐 PIN lookup result:', { data: data?.id, error });
    
    if (error) return { data: null, error };
    
    // Create a session for PIN-based auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: pin // In production, use proper password hashing
    });
    
    console.log('🔐 PIN auth result:', { authData: authData?.user?.id, authError });
    return { data: authData, error: authError };
  },

  signOut: async () => {
    console.log('🔐 Signing out');
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  getCurrentUser: async () => {
    console.log('🔐 Getting current user');
    const { data: { user }, error } = await supabase.auth.getUser();
    console.log('🔐 Current user result:', { user: user?.id, error });
    return { user, error };
  }
};

// Pick List Services
export const pickListService = {
  getAll: async () => {
    const { data, error } = await supabase
      .from('pick_lists')
      .select(`
        *,
        assigned_to_user:users(id, email, firstname, lastname)
      `)
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  getById: async (id: string) => {
    const { data, error } = await supabase
      .from('pick_lists')
      .select(`
        *,
        assigned_to_user:users(id, email, firstname, lastname),
        pick_items(*)
      `)
      .eq('id', id)
      .single();
    
    return { data, error };
  },

  getByUser: async (userId: string) => {
    console.log('📋 Supabase Service: Loading pick lists for user:', userId);
    
    // Check if we have a valid user ID
    if (!userId) {
      console.error('📋 No user ID provided');
      return { data: [], error: null };
    }
    
    // Skip user verification for demo users to avoid database errors
    // In production, you would verify the user exists in the database
    console.log('📋 Proceeding with pick list query for user:', userId);
    
    // Get all pick lists for this user
    const { data, error } = await supabase
      .from('pick_lists')
      .select('*')
      .eq('assigned_to', userId)
      .order('created_at', { ascending: false });
    
    console.log('📋 Pick lists query result:', { 
      dataCount: data?.length, 
      error: error?.message, 
      userId,
      sampleData: data?.slice(0, 3)?.map(item => ({ 
        id: item.id, 
        order_number: item.order_number, 
        status: item.status,
        assigned_to: item.assigned_to,
        customer_name: item.customer_name,
        total_items: item.total_items,
        picked_items: item.picked_items
      })),
      query: `assigned_to = ${userId}`
    });
    
    // Also try to get ALL pick lists to see what's in the database
    const { data: allData, error: allError } = await supabase
      .from('pick_lists')
      .select('*')
      .limit(10);
    
    console.log('📋 All pick lists in database:', { 
      allDataCount: allData?.length,
      allError: allError?.message,
      allSample: allData?.slice(0, 3)?.map(item => ({
        id: item.id,
        order_number: item.order_number,
        assigned_to: item.assigned_to,
        status: item.status
      }))
    });
    
    return { data, error };
  },

  create: async (pickList: Omit<PickList, 'id' | 'createdAt' | 'updatedAt'>) => {
    const { data, error } = await supabase
      .from('pick_lists')
      .insert({
        order_number: pickList.orderNumber,
        status: pickList.status,
        assigned_to: pickList.assignedTo,
        priority: pickList.priority,
        customer_name: pickList.customerName,
        total_items: pickList.totalItems,
        picked_items: pickList.pickedItems,
        estimated_time: pickList.estimatedTime,
        due_date: pickList.dueDate
      })
      .select()
      .single();
    
    return { data, error };
  },

  update: async (id: string, updates: Partial<PickList>) => {
    const { data, error } = await supabase
      .from('pick_lists')
      .update({
        status: updates.status,
        picked_items: updates.pickedItems,
        actual_time: updates.actualTime,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },

  delete: async (id: string) => {
    const { error } = await supabase
      .from('pick_lists')
      .delete()
      .eq('id', id);
    
    return { error };
  }
};

// Pick Item Services
export const pickItemService = {
  getByPickList: async (pickListId: string) => {
    const { data, error } = await supabase
      .from('pick_items')
      .select('*')
      .eq('pick_list_id', pickListId)
      .order('created_at');
    
    return { data, error };
  },

  update: async (id: string, updates: Partial<PickItem>) => {
    const { data, error } = await supabase
      .from('pick_items')
      .update({
        quantity_picked: updates.quantityPicked,
        status: updates.status,
        notes: updates.notes,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },

  bulkUpdate: async (updates: Array<{ id: string; updates: Partial<PickItem> }>) => {
    const promises = updates.map(({ id, updates }) => 
      pickItemService.update(id, updates)
    );
    
    const results = await Promise.all(promises);
    return results;
  }
};

// Performance Metrics Services
export const metricsService = {
  getUserMetrics: async (userId: string, dateRange?: { start: string; end: string }) => {
    const query = supabase
      .from('pick_lists')
      .select('*')
      .eq('assigned_to', userId)
      .eq('status', 'completed');
    
    if (dateRange) {
      query.gte('updated_at', dateRange.start)
          .lte('updated_at', dateRange.end);
    }
    
    const { data, error } = await query;
    
    if (error) return { data: null, error };
    
    // Calculate metrics
    const metrics: PerformanceMetrics = {
      totalPicksToday: data.length,
      averagePickTime: data.reduce((acc, item) => acc + (item.actual_time || 0), 0) / data.length,
      accuracy: 95, // Calculate based on actual vs expected
      efficiency: 85, // Calculate based on time metrics
      completedOrders: data.length,
      pendingOrders: 0 // Will be calculated separately
    };
    
    return { data: metrics, error: null };
  },

  getTeamMetrics: async (dateRange?: { start: string; end: string }) => {
    const query = supabase
      .from('pick_lists')
      .select(`
        *,
        assigned_to_user:users(id, email, firstName, lastName)
      `);
    
    if (dateRange) {
      query.gte('updated_at', dateRange.start)
          .lte('updated_at', dateRange.end);
    }
    
    const { data, error } = await query;
    return { data, error };
  }
};

// Real-time subscriptions
export const subscriptionService = {
  subscribeToPickLists: (userId: string, callback: (payload: any) => void) => {
    return supabase
      .channel('pick_lists')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pick_lists',
          filter: `assigned_to=eq.${userId}`
        },
        callback
      )
      .subscribe();
  },

  subscribeToPickItems: (pickListId: string, callback: (payload: any) => void) => {
    return supabase
      .channel('pick_items')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pick_items',
          filter: `pick_list_id=eq.${pickListId}`
        },
        callback
      )
      .subscribe();
  }
};